import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { reconciliationService } from '../services/api';
import { format } from 'date-fns';

function ReconciliationDashboard({ user }) {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [issues, setIssues] = useState([]);
  const [logs, setLogs] = useState([]);
  const [unreconciled, setUnreconciled] = useState([]);
  const [mismatches, setMismatches] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [lastRunInfo, setLastRunInfo] = useState(null);

  const [filters, setFilters] = useState({
    status: '',
    source: '',
    fromDate: '',
    toDate: '',
    search: '',
    requiresManualReview: '',
  });

  const canTrigger = ['PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  // Load summary
  const loadSummary = useCallback(async () => {
    try {
      const res = await reconciliationService.getDashboardSummary();
      if (res?.success) setSummary(res.data);
      else if (res?.data) setSummary(res.data);
    } catch (err) {
      console.error('Failed to load reconciliation summary:', err);
    }
  }, []);

  // Load issues
  const loadIssues = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (filters.status) params.status = filters.status;
      if (filters.source) params.source = filters.source;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.search) params.search = filters.search;
      if (filters.requiresManualReview === 'true') params.requiresManualReview = true;
      if (filters.requiresManualReview === 'false') params.requiresManualReview = false;

      const res = await reconciliationService.getPaymentIssues(params);
      const data = res?.data || res;
      setIssues(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      setPagination(prev => ({
        ...prev,
        page,
        total: data?.pagination?.totalItems || data?.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to load payment issues:', err);
      toast.error('Failed to load payment issues');
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Load logs
  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const res = await reconciliationService.getLogs(params);
      const data = res?.data || res;
      setLogs(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      if (data?.lastRunInfo) setLastRunInfo(data.lastRunInfo);
      setPagination(prev => ({
        ...prev,
        page,
        total: data?.pagination?.totalItems || data?.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to load reconciliation logs:', err);
      toast.error('Failed to load reconciliation logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Load unreconciled
  const loadUnreconciled = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.search) params.search = filters.search;

      const res = await reconciliationService.getUnreconciledPayments(params);
      const data = res?.data || res;
      setUnreconciled(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      setPagination(prev => ({
        ...prev,
        page,
        total: data?.pagination?.totalItems || data?.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to load unreconciled payments:', err);
      toast.error('Failed to load unreconciled payments');
      setUnreconciled([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Load mismatches
  const loadMismatches = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const res = await reconciliationService.getAmountMismatches(params);
      const data = res?.data || res;
      setMismatches(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      setPagination(prev => ({
        ...prev,
        page,
        total: data?.pagination?.totalItems || data?.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to load amount mismatches:', err);
      toast.error('Failed to load amount mismatches');
      setMismatches([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Load issue detail
  const loadIssueDetail = async (id) => {
    try {
      const res = await reconciliationService.getPaymentIssueDetail(id);
      const data = res?.data || res;
      setSelectedIssue(data);
    } catch (err) {
      console.error('Failed to load issue detail:', err);
      toast.error('Failed to load issue detail');
    }
  };

  // Trigger reconciliation
  const handleTrigger = async (type) => {
    if (!canTrigger) return;
    setTriggerLoading(true);
    try {
      await reconciliationService.triggerReconciliation({ type });
      toast.success(`${type === 'quick' ? 'Quick' : 'Deep'} reconciliation triggered`);
      loadSummary();
    } catch (err) {
      console.error('Failed to trigger reconciliation:', err);
      toast.error('Failed to trigger reconciliation');
    } finally {
      setTriggerLoading(false);
    }
  };

  // Load data based on active sub-tab
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1, total: 0 }));
    switch (activeSubTab) {
      case 'issues': loadIssues(1); break;
      case 'logs': loadLogs(1); break;
      case 'unreconciled': loadUnreconciled(1); break;
      case 'mismatches': loadMismatches(1); break;
      default: break;
    }
  }, [activeSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh on filter change
  useEffect(() => {
    switch (activeSubTab) {
      case 'issues': loadIssues(1); break;
      case 'logs': loadLogs(1); break;
      case 'unreconciled': loadUnreconciled(1); break;
      case 'mismatches': loadMismatches(1); break;
      default: break;
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh summary every 60s
  useEffect(() => {
    const interval = setInterval(loadSummary, 60000);
    return () => clearInterval(interval);
  }, [loadSummary]);

  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    switch (activeSubTab) {
      case 'issues': loadIssues(newPage); break;
      case 'logs': loadLogs(newPage); break;
      case 'unreconciled': loadUnreconciled(newPage); break;
      case 'mismatches': loadMismatches(newPage); break;
      default: break;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'FAILED': { bg: '#F2D9D3', color: '#5F241B' },
      'QUEUED': { bg: '#F3E6CB', color: '#684A1F' },
      'PROCESSING': { bg: '#E1E9E2', color: '#0F2619' },
      'COMPLETED': { bg: '#E1E9E2', color: '#0F2619' },
      'PENDING': { bg: '#F3E6CB', color: '#684A1F' },
      'SUCCESS': { bg: '#E1E9E2', color: '#0F2619' },
    };
    const s = colors[status] || { bg: '#EEEBE1', color: '#36433B' };
    return (
      <span style={{
        backgroundColor: s.bg,
        color: s.color,
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
      }}>
        {status}
      </span>
    );
  };

  const subTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'issues', label: 'Payment Issues' },
    { id: 'logs', label: 'Reconciliation Logs' },
    { id: 'unreconciled', label: 'Unreconciled' },
    { id: 'mismatches', label: 'Amount Mismatches' },
  ];

  // ── Summary Cards ──
  const renderSummaryCards = () => {
    if (!summary) return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
        Loading summary...
      </div>
    );

    const { overview, reconciliation, activity } = summary;
    const cards = [
      { label: 'Total Processing Logs', value: overview?.totalProcessingLogs ?? 0, color: '#1A3E2A' },
      { label: 'Completed', value: overview?.completed ?? 0, color: '#1A3E2A' },
      { label: 'Failed', value: overview?.failed ?? 0, color: '#A13F2F' },
      { label: 'Queued / Processing', value: `${overview?.queued ?? 0} / ${overview?.processing ?? 0}`, color: '#B68235' },
      { label: 'Manual Review', value: overview?.requiresManualReview ?? 0, color: '#A9613C' },
      { label: 'Reconciled', value: reconciliation?.reconciledPayments ?? 0, color: '#163626' },
      { label: 'Unreconciled', value: reconciliation?.unreconciledPayments ?? 0, color: '#8E3628' },
      { label: 'Last 24h Activity', value: activity?.last24Hours ?? 0, color: '#1A3E2A' },
    ];

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {cards.map((c, i) => (
          <div key={i} style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderLeft: `4px solid ${c.color}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '28px', fontWeight: '700', color: c.color }}>{c.value}</span>
            </div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#5F6B61', fontWeight: '500' }}>
              {c.label}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Filter Bar ──
  const renderFilters = () => (
    <div style={{
      display: 'flex',
      gap: '12px',
      marginBottom: '16px',
      flexWrap: 'wrap',
      alignItems: 'center',
    }}>
      {(activeSubTab === 'issues' || activeSubTab === 'unreconciled') && (
        <input
          type="text"
          placeholder="Search Payment ID / Order ID..."
          value={filters.search}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          style={inputStyle}
        />
      )}
      {activeSubTab === 'issues' && (
        <>
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Statuses</option>
            <option value="FAILED">Failed</option>
            <option value="QUEUED">Queued</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            value={filters.source}
            onChange={(e) => setFilters(f => ({ ...f, source: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Sources</option>
            <option value="webhook">Webhook</option>
            <option value="client">Client</option>
            <option value="reconciliation">Reconciliation</option>
          </select>
          <select
            value={filters.requiresManualReview}
            onChange={(e) => setFilters(f => ({ ...f, requiresManualReview: e.target.value }))}
            style={inputStyle}
          >
            <option value="">Manual Review: All</option>
            <option value="true">Needs Review</option>
            <option value="false">No Review Needed</option>
          </select>
        </>
      )}
      {activeSubTab === 'logs' && (
        <select
          value={filters.status}
          onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          style={inputStyle}
        >
          <option value="">All Statuses</option>
          <option value="FAILED">Failed</option>
          <option value="QUEUED">Queued</option>
          <option value="PROCESSING">Processing</option>
          <option value="COMPLETED">Completed</option>
        </select>
      )}
      <input
        type="date"
        value={filters.fromDate}
        onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value }))}
        style={inputStyle}
        title="From Date"
      />
      <input
        type="date"
        value={filters.toDate}
        onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value }))}
        style={inputStyle}
        title="To Date"
      />
      <button
        onClick={() => setFilters({ status: '', source: '', fromDate: '', toDate: '', search: '', requiresManualReview: '' })}
        style={{
          padding: '8px 14px',
          borderRadius: '6px',
          border: '1px solid #D2CDBF',
          backgroundColor: '#F5F3EB',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '500',
        }}
      >
        Clear Filters
      </button>
    </div>
  );

  // ── Pagination ──
  const renderPagination = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '16px',
      padding: '12px 0',
    }}>
      <span style={{ fontSize: '13px', color: '#5F6B61' }}>
        Showing page {pagination.page} of {totalPages} ({pagination.total} total)
      </span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          disabled={pagination.page <= 1}
          onClick={() => handlePageChange(pagination.page - 1)}
          style={{ ...paginationBtnStyle, opacity: pagination.page <= 1 ? 0.5 : 1 }}
        >
          ← Previous
        </button>
        <button
          disabled={pagination.page >= totalPages}
          onClick={() => handlePageChange(pagination.page + 1)}
          style={{ ...paginationBtnStyle, opacity: pagination.page >= totalPages ? 0.5 : 1 }}
        >
          Next →
        </button>
      </div>
    </div>
  );

  // ── Issues Table ──
  const renderIssuesTable = () => (
    <div>
      {renderFilters()}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>Loading...</div>
      ) : issues.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>No payment issues found</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Payment ID</th>
                  <th style={thStyle}>Guest</th>
                  <th style={thStyle}>Booking #</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Attempts</th>
                  <th style={thStyle}>Review</th>
                  <th style={thStyle}>Queued At</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr key={issue.id} style={{ borderBottom: '1px solid #EEEBE1' }}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{issue.paymentId || '—'}</span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{issue.booking?.guest?.name || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#5F6B61' }}>{issue.booking?.guest?.email || ''}</div>
                      <div style={{ fontSize: '11px', color: '#5F6B61' }}>{issue.booking?.guest?.phone || ''}</div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{issue.booking?.bookingNumber || '—'}</span>
                    </td>
                    <td style={tdStyle}>{formatCurrency(issue.booking?.totalPrice)}</td>
                    <td style={tdStyle}>{getStatusBadge(issue.status)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        backgroundColor: '#F0F3EE',
                        color: '#122E20',
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}>
                        {issue.source || '—'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '13px' }}>{issue.attempts ?? 0} / {issue.maxAttempts ?? 5}</span>
                    </td>
                    <td style={tdStyle}>
                      {issue.requiresManualReview ? (
                        <span style={{ backgroundColor: '#F3E6CB', color: '#684A1F', padding: '2px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600' }}>
                          Yes
                        </span>
                      ) : (
                        <span style={{ color: '#5F6B61', fontSize: '12px' }}>No</span>
                      )}
                    </td>
                    <td style={tdStyle}>{formatDate(issue.queuedAt)}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => loadIssueDetail(issue.id)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          backgroundColor: '#1A3E2A',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '500',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );

  // ── Issue Detail Modal ──
  const renderIssueDetail = () => {
    if (!selectedIssue) return null;
    const issue = selectedIssue;

    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#E9DFCE',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '700px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: '#1A3E2A' }}>Payment Issue Detail</h3>
            <button
              onClick={() => setSelectedIssue(null)}
              style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#5F6B61' }}
            >
              ✕
            </button>
          </div>

          {/* Payment Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <InfoItem label="Payment ID" value={issue.paymentId} mono />
            <InfoItem label="Order ID" value={issue.orderId} mono />
            <InfoItem label="Status" value={issue.status} badge />
            <InfoItem label="Source" value={issue.source} />
            <InfoItem label="Attempts" value={`${issue.attempts ?? 0} / ${issue.maxAttempts ?? 5}`} />
            <InfoItem label="Webhook Received" value={issue.webhookReceived ? 'Yes' : 'No'} />
            <InfoItem label="Webhook At" value={formatDate(issue.webhookReceivedAt)} />
            <InfoItem label="Queued At" value={formatDate(issue.queuedAt)} />
          </div>

          {issue.lastError && (
            <div style={{
              backgroundColor: '#F9EEEB',
              border: '1px solid #E5B3A9',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#5F241B', marginBottom: '4px' }}>Last Error</div>
              <div style={{ fontSize: '13px', color: '#772D21', fontFamily: 'monospace' }}>{issue.lastError}</div>
            </div>
          )}

          {/* Booking Info */}
          {issue.booking && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '14px', color: '#36433B', marginBottom: '10px', borderBottom: '1px solid #E3DFD3', paddingBottom: '6px' }}>
                Booking Details
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <InfoItem label="Booking #" value={issue.booking.bookingNumber} mono />
                <InfoItem label="Guest" value={issue.booking.guest?.name} />
                <InfoItem label="Email" value={issue.booking.guest?.email} />
                <InfoItem label="Phone" value={issue.booking.guest?.phone} />
                <InfoItem label="Check-in" value={formatDate(issue.booking.checkIn)} />
                <InfoItem label="Check-out" value={formatDate(issue.booking.checkOut)} />
                <InfoItem label="Total Price" value={formatCurrency(issue.booking.totalPrice)} />
                <InfoItem label="Payment Status" value={issue.booking.paymentStatus} badge />
              </div>
              {issue.booking.rooms?.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#5F6B61' }}>
                  <strong>Rooms:</strong> {issue.booking.rooms.map(r => `${r.roomNumber} (${r.roomTypeName})`).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          {issue.timeline?.length > 0 && (
            <div>
              <h4 style={{ fontSize: '14px', color: '#36433B', marginBottom: '10px', borderBottom: '1px solid #E3DFD3', paddingBottom: '6px' }}>
                Event Timeline
              </h4>
              <div style={{ position: 'relative', paddingLeft: '20px' }}>
                {issue.timeline.map((event, idx) => (
                  <div key={idx} style={{
                    position: 'relative',
                    paddingBottom: idx === issue.timeline.length - 1 ? 0 : '16px',
                    borderLeft: idx === issue.timeline.length - 1 ? 'none' : '2px solid #E3DFD3',
                    paddingLeft: '16px',
                    marginLeft: '-1px',
                  }}>
                    <div style={{
                      position: 'absolute',
                      left: '-7px',
                      top: '2px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: event.event?.toLowerCase().includes('error') ? '#A13F2F' : '#1A3E2A',
                      border: '2px solid white',
                      boxShadow: '0 0 0 2px #E3DFD3',
                    }} />
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A3E2A' }}>{event.event}</div>
                    <div style={{ fontSize: '12px', color: '#5F6B61' }}>{formatDate(event.timestamp)}</div>
                    {event.details && (
                      <div style={{ fontSize: '12px', color: '#A5A89B', marginTop: '2px' }}>{event.details}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Logs Table ──
  const renderLogsTable = () => (
    <div>
      {lastRunInfo && (
        <div style={{
          display: 'flex',
          gap: '24px',
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#F0F3EE',
          borderRadius: '8px',
          border: '1px solid #C4D4C8',
          fontSize: '13px',
        }}>
          <span><strong>Last Quick Reconciliation:</strong> {formatDate(lastRunInfo.lastQuickReconciliation)}</span>
          <span><strong>Last Deep Reconciliation:</strong> {formatDate(lastRunInfo.lastDeepReconciliation)}</span>
        </div>
      )}
      {renderFilters()}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>Loading...</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>No reconciliation logs found</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Payment ID</th>
                  <th style={thStyle}>Order ID</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Attempts</th>
                  <th style={thStyle}>Last Error</th>
                  <th style={thStyle}>Queued At</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #EEEBE1' }}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{log.paymentId || '—'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{log.orderId || '—'}</span>
                    </td>
                    <td style={tdStyle}>{getStatusBadge(log.status)}</td>
                    <td style={tdStyle}>{log.source || '—'}</td>
                    <td style={tdStyle}>{log.attempts ?? 0} / {log.maxAttempts ?? 5}</td>
                    <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.lastError || '—'}
                    </td>
                    <td style={tdStyle}>{formatDate(log.queuedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );

  // ── Unreconciled Table ──
  const renderUnreconciledTable = () => (
    <div>
      {renderFilters()}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>Loading...</div>
      ) : unreconciled.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>No unreconciled payments found</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Payment ID</th>
                  <th style={thStyle}>Transaction ID</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Booking #</th>
                  <th style={thStyle}>Guest</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Created At</th>
                </tr>
              </thead>
              <tbody>
                {unreconciled.map((item, idx) => (
                  <tr key={item.id || idx} style={{ borderBottom: '1px solid #EEEBE1' }}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.paymentNumber || item.id || '—'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.transactionId || '—'}</span>
                    </td>
                    <td style={tdStyle}>{formatCurrency(item.amount)}</td>
                    <td style={tdStyle}>{item.booking?.bookingNumber || '—'}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{item.booking?.guest?.name || item.guest?.name || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#5F6B61' }}>{item.booking?.guest?.email || item.guest?.email || ''}</div>
                    </td>
                    <td style={tdStyle}>{getStatusBadge(item.status || 'PENDING')}</td>
                    <td style={tdStyle}>{formatDate(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );

  // ── Mismatches Table ──
  const renderMismatchesTable = () => (
    <div>
      {renderFilters()}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>Loading...</div>
      ) : mismatches.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>No amount mismatches found</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Payment #</th>
                  <th style={thStyle}>Transaction ID</th>
                  <th style={thStyle}>Recorded Amount</th>
                  <th style={thStyle}>Base Amount</th>
                  <th style={thStyle}>Difference</th>
                  <th style={thStyle}>Booking #</th>
                  <th style={thStyle}>Guest</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((item, idx) => (
                  <tr key={item.id || idx} style={{ borderBottom: '1px solid #EEEBE1' }}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.paymentNumber || '—'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.transactionId || '—'}</span>
                    </td>
                    <td style={tdStyle}>{formatCurrency(item.recordedAmount)}</td>
                    <td style={tdStyle}>{formatCurrency(item.baseAmount)}</td>
                    <td style={tdStyle}>
                      <span style={{
                        color: '#8E3628',
                        fontWeight: '700',
                        backgroundColor: '#F9EEEB',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '13px',
                      }}>
                        {formatCurrency(item.difference)}
                      </span>
                    </td>
                    <td style={tdStyle}>{item.booking?.bookingNumber || '—'}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{item.booking?.guest?.name || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#5F6B61' }}>{item.booking?.guest?.email || ''}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );

  // ── Overview Tab ──
  const renderOverview = () => (
    <div>
      {renderSummaryCards()}

      {/* Trigger Reconciliation */}
      {canTrigger && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '16px', margin: '0 0 12px 0', color: '#1A3E2A' }}>Trigger Reconciliation</h3>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => handleTrigger('quick')}
              disabled={triggerLoading}
              style={{
                padding: '10px 20px',
                backgroundColor: triggerLoading ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: triggerLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px',
              }}
            >
              {triggerLoading ? 'Running...' : 'Quick Reconciliation'}
            </button>
            <button
              onClick={() => handleTrigger('deep')}
              disabled={triggerLoading}
              style={{
                padding: '10px 20px',
                backgroundColor: triggerLoading ? '#A5A89B' : '#A9613C',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: triggerLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px',
              }}
            >
              {triggerLoading ? 'Running...' : 'Deep Reconciliation'}
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#A5A89B', marginTop: '8px', marginBottom: 0 }}>
            Quick: scans last 2 hours · Deep: scans last 7 days
          </p>
        </div>
      )}

      {/* Schedule info */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ fontSize: '16px', margin: '0 0 12px 0', color: '#1A3E2A' }}>Reconciliation Schedule</h3>
        <table style={{ ...tableStyle, marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={thStyle}>Job</th>
              <th style={thStyle}>Schedule</th>
              <th style={thStyle}>Scope</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #EEEBE1' }}>
              <td style={tdStyle}>Quick Reconciliation</td>
              <td style={tdStyle}>Every 5 minutes</td>
              <td style={tdStyle}>Last 2 hours of Razorpay payments</td>
            </tr>
            <tr>
              <td style={tdStyle}>Deep Reconciliation</td>
              <td style={tdStyle}>Daily at 2:00 AM IST</td>
              <td style={tdStyle}>Last 7 days of Razorpay payments</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', color: '#1A3E2A' }}>
          Payment Reconciliation
        </h2>
        <p style={{ margin: 0, fontSize: '14px', color: '#5F6B61' }}>
          Monitor payment reconciliation between Razorpay and PMS database
        </p>
      </div>

      {/* Sub-tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        flexWrap: 'wrap',
      }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: activeSubTab === tab.id ? '#1A3E2A' : '#EEEBE1',
              color: activeSubTab === tab.id ? 'white' : '#36433B',
              transition: 'all 0.2s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        minHeight: '300px',
      }}>
        {activeSubTab === 'overview' && renderOverview()}
        {activeSubTab === 'issues' && renderIssuesTable()}
        {activeSubTab === 'logs' && renderLogsTable()}
        {activeSubTab === 'unreconciled' && renderUnreconciledTable()}
        {activeSubTab === 'mismatches' && renderMismatchesTable()}
      </div>

      {/* Issue Detail Modal */}
      {renderIssueDetail()}
    </div>
  );
}

// ── Helper Components ──
function InfoItem({ label, value, mono, badge }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: '#A5A89B', fontWeight: '500', marginBottom: '2px' }}>{label}</div>
      {badge ? (
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: value === 'COMPLETED' || value === 'SUCCESS' ? '#E1E9E2' : value === 'FAILED' ? '#F2D9D3' : '#F3E6CB',
          color: value === 'COMPLETED' || value === 'SUCCESS' ? '#0F2619' : value === 'FAILED' ? '#5F241B' : '#684A1F',
        }}>
          {value || '—'}
        </span>
      ) : (
        <div style={{
          fontSize: '13px',
          color: '#1A3E2A',
          fontWeight: '500',
          fontFamily: mono ? 'monospace' : 'inherit',
        }}>
          {value || '—'}
        </div>
      )}
    </div>
  );
}

// ── Shared Styles ──
const inputStyle = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #D2CDBF',
  fontSize: '13px',
  backgroundColor: '#F5F3EB',
  outline: 'none',
  minWidth: '150px',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  backgroundColor: '#F5F3EB',
  borderBottom: '2px solid #E3DFD3',
  fontSize: '12px',
  fontWeight: '600',
  color: '#5F6B61',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'top',
};

const paginationBtnStyle = {
  padding: '6px 14px',
  border: '1px solid #D2CDBF',
  borderRadius: '6px',
  backgroundColor: 'white',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
};

export default ReconciliationDashboard;
